import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  Button,
  useMediaQuery,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory } from "react-router-dom";
import {
  settingBackPathState,
  platformState,
  userInfoState,
} from "../global-state";
import useApiCall from "../common/api-call";
import {
  getDesktopDevice,
  registerDesktopDevice,
  deleteDesktopDevice,
} from "./desktop-permission-service";
import { useToast } from "../common/toast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingSpinner from "../common/loading";

const DesktopPermission = () => {
  const theme = useTheme();
  const history = useHistory();
  const platform = useRecoilValue(platformState);
  const userInfo = useRecoilValue(userInfoState);
  const { apiCall } = useApiCall();
  const { successToast, errorToast } = useToast();
  const isDesktop = platform?.source === "desktop";
  const [settingBackPath, setSettingBackPath] =
    useRecoilState(settingBackPathState);
  const isSmallScreen = useMediaQuery("(max-width:768px)");

  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    categories: [],
    capabilities: [],
  });

  const fetchDeviceInfo = async () => {
    if (!userInfo?.activeScope?.id) return;
    setIsLoading(true);
    try {
      const deviceId = platform.info.deviceId;
      const response = await getDesktopDevice(
        apiCall,
        userInfo.activeScope.id,
        deviceId
      );
      if (response) {
        setDeviceInfo(response);
        setPermissions({
          categories: response.categories || [],
          capabilities: response.supportedCapabilities || [],
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!userInfo?.activeScope?.id) return;

    setIsRegistering(true);
    try {
      if (!platform.info) {
        errorToast("Desktop information not available");
        setIsRegistering(false);
        return;
      }

      const deviceData = {
        ...platform.info,
        scopeId: userInfo.activeScope.id,
        status: "active",
      };

      const response = await registerDesktopDevice(apiCall, deviceData);
      if (response) {
        successToast("Success");
        await fetchDeviceInfo();
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error!");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDeregister = async () => {
    if (!userInfo?.activeScope?.id || !platform.info?.deviceId) {
      errorToast("Error!");
      return;
    }

    try {
      const response = await deleteDesktopDevice(
        apiCall,
        userInfo.activeScope.id,
        platform.info.deviceId
      );

      if (response) {
        successToast("Deregistered");
        setDeviceInfo(null);
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      console.error("Error deregistering desktop:", error);
      errorToast("Error!");
    }
  };

  const handlePermissionChange = (type) => {
    // TODO: Implement permission change functionality
    console.log(`Permission ${type} changed`);
  };

  useEffect(() => {
    if (isDesktop) {
      fetchDeviceInfo();
    }
  }, [isDesktop, userInfo?.activeScope?.id]);

  if (!isDesktop) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography variant="h6">
          This feature is only available in the desktop app.
        </Typography>
      </Box>
    );
  }

  if (!deviceInfo) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        p={2}
        width="100%"
      >
        {isSmallScreen && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                if (settingBackPath) {
                  history.replace(settingBackPath);
                  setSettingBackPath("");
                } else {
                  history.replace("/setting");
                }
              }}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Desktop Permission</Typography>
          </Box>
        )}
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleRegister}
                disabled={isRegistering || !platform.info}
                sx={{ textTransform: "none" }}
              >
                {isRegistering ? "Registering..." : "Register this Desktop"}
              </Button>
              {!platform.info && (
                <Typography variant="caption" color="error" sx={{ mt: 2 }}>
                  Desktop information not available. Please restart the
                  application.
                </Typography>
              )}
            </Box>
            <Card
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: "12px",
                border: "1px solid #EAEEF0",
                boxShadow: "none",
                width: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <InfoOutlinedIcon
                    sx={{ color: theme.palette.primary.main, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      About Desktop Registration
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Register your desktop to enable desktop-specific features
                      and notifications. This allows the application to interact
                      with your desktop environment and provide enhanced
                      functionality.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" p={2} width="100%">
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {isSmallScreen && (
          <IconButton
            color="inherit"
            edge="start"
            sx={{ mr: 1 }}
            onClick={() => history.replace("/setting")}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6">Desktop Permission</Typography>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Card
            sx={{
              borderRadius: "12px",
              border: "1px solid #EAEEF0",
              mb: 2,
              boxShadow: "none",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Desktop Name
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {deviceInfo?.deviceName || "Unknown Device"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeregister}
                  sx={{ textTransform: "none" }}
                >
                  Deregister
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Categories
          </Typography>
          <Grid container spacing={1}>
            {permissions.categories.length > 0 ? (
              permissions.categories.map((category, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 1.5,
                      borderRadius: "12px",
                      border: "1px solid #EAEEF0",
                      boxShadow: "none",
                      backgroundColor: theme.palette.background.paper,
                      opacity: 1,
                      transition: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {category}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {`Enable ${category} features`}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={true}
                            onChange={() => handlePermissionChange(category)}
                            disabled={true}
                          />
                        }
                        label=""
                        onClick={(event) => event.stopPropagation()}
                      />
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 1.5,
                    borderRadius: "12px",
                    border: "1px solid #EAEEF0",
                    boxShadow: "none",
                    backgroundColor: theme.palette.background.paper,
                    opacity: 1,
                    transition: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      height: "80px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Empty data categories
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "12px",
              border: "1px solid #EAEEF0",
              boxShadow: "none",
              mt: 2,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    color: theme.palette.primary.main,
                    mr: 2,
                  }}
                />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Caution! By registering, you are exposing this desktop to
                    the current space. Choose the permission categories to
                    control the specific data to be shared with mHome service
                    and the space.
                    <br />
                    For example, by just registering, users on the space will
                    know the desktop exists, but no real data is exposed.
                    <br />
                    But if you enabled the &quot;File System&quot; category,
                    automations can operate your local files, which also means
                    other users in the space have the access to your files.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default DesktopPermission;
